import React, { useState, useEffect, useRef } from 'react';
import { useUser } from './UserProvider';
import useAdminVerification from '../hooks/useAdminVerification';
import AdminModal from './AdminModal';
import '../css.css';

const AdminDashboard = () => {
    const { user, getAuthHeaders, refreshUserInfo, isAdmin } = useUser();
    const { isAdminVerified, verifyAdminAccess, loading: adminVerifyLoading } = useAdminVerification();
    
    // URL 파라미터에서 초기 탭 상태 읽기
    const getInitialTab = () => {
        const params = new URLSearchParams(window.location.search);
        return params.get('tab') || 'users';
    };
    
    const getInitialMapView = () => {
        const params = new URLSearchParams(window.location.search);
        return params.get('mapView') || 'overview';
    };
    
    const [activeTab, setActiveTab] = useState(getInitialTab());
    const [users, setUsers] = useState([]);
    const [deletedUsers, setDeletedUsers] = useState([]);
    const [comments, setComments] = useState([]);
    const [stats, setStats] = useState({});
    const [pageStats, setPageStats] = useState([]);
    const [dailyStats, setDailyStats] = useState([]);
    const [mapLocations, setMapLocations] = useState([]);
    const [mapLocationStats, setMapLocationStats] = useState({
        userStats: [],
        mapStats: [],
        totalLocations: 0
    });
    const [mapStatsView, setMapStatsView] = useState(getInitialMapView());
    const [baitRecipes, setBaitRecipes] = useState([]);
    const [baitRecipeStats, setBaitRecipeStats] = useState({
        total_recipes: 0,
        unique_users: 0,
        last_created_date: null
    });
    const [userActivity, setUserActivity] = useState({
        userActivity: [],
        ipActivity: [],
        pageActivity: [],
        dailyActivity: [],
        summary: {}
    });
    const [activityPeriod, setActivityPeriod] = useState('week');
    const [activityView, setActivityView] = useState('overview');
    const [chartPeriod, setChartPeriod] = useState('week');
    const [bannerFormData, setBannerFormData] = useState({});
    const [selectedBannerType, setSelectedBannerType] = useState('default');
    const [collapsedSections, setCollapsedSections] = useState({
        banner: true,
        button: true,
        other: true
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [modal, setModal] = useState({
        isOpen: false,
        type: 'info',
        title: '',
        message: '',
        onConfirm: null
    });

    // 시간/분 input refs
    const hourInputRef = useRef(null);
    const minuteInputRef = useRef(null);

    // 지도명 변환 함수
    const getKoreanMapName = (englishName) => {
        const mapMapping = {
            'mosquito_lake_l': '모기 호수',
            'american_pond_l': '엘크 호수',
            'winding_rivulet_l': '굽이치는 개울',
            'old_burg_lake_l': '올드 버그 호수',
            'belaya_river_l': '벨라야 강',
            'kuori_lake_l': '쿠오리 호수',
            'bear_lake_l': '곰 호수',
            'volkhov_river_l': '볼호프 강',
            'seversky_donets_river_l': '세베르스키 도네츠 강',
            'sura_river_l': '수라 강',
            'ladoga_lake_l': '라도가 호수',
            'the_amber_lake_l': '엠버 호수',
            'ladoga_archipelago_l': '라도가 군도',
            'akhtuba_river_l': '악투바 강',
            'copper_lake_l': '카퍼 호수',
            'lower_tunguska_river_l': '니즈냐야 퉁구스카 강',
            'yama_river_l': '야마 강',
            'norwegian_sea_l': '노르웨이 해'
        };

        // 영문명을 그대로 키로 사용
        return mapMapping[englishName] || englishName;
    };

    // 페이지 경로를 메뉴명으로 변환하는 함수
    const getPageLabel = (pagePath) => {
        const pageMapping = {
            'home': '메인페이지',
            'reels': '릴 정보',
            'rod': '낚싯대 정보',
            'line': '낚싯줄 정보',
            'hooks': '바늘 하중 정보',
            'interactive_maps': '반응형 지도',
            'records': '기록실',
            'cafeorder': '카페 오더',
            'oil': '릴 오일 정보',
            'drink': '술 정보',
            'fishes': '어종 번역',
            'groundbaits': '미끼 밑밥 번역',
            'baitcalculator': '미끼 밑밥 계산기',
            'trophy': '트로피 사이즈',
            'duration': '내구도 계산기',
            'ordertimer': '오더 & 어망 타이머',
            'food': '수제음식 레시피',
            'lure': '수제루어 레시피',
            'price': '수역별 시세',
            'tacklexp': '채비별 경험치 배율',
            'xptable': 'XP',
            'skill': '스킬 정리',
            'qualification': '자격 정리',
            'updatelog': '업데이트 기록',
            'vklink': 'VK수역별 링크'
        };

        return pageMapping[pagePath] || pagePath;
    };

    // URL 업데이트 함수
    const updateURL = (tab, mapView = null) => {
        const params = new URLSearchParams();
        params.set('tab', tab);
        if (mapView && tab === 'map-locations') {
            params.set('mapView', mapView);
        }
        const newURL = `${window.location.pathname}?${params.toString()}`;
        window.history.pushState({ tab, mapView }, '', newURL);
    };

    // 탭 변경 핸들러
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        updateURL(tab, tab === 'map-locations' ? mapStatsView : null);
    };

    // 지도 뷰 변경 핸들러
    const handleMapViewChange = (view) => {
        setMapStatsView(view);
        updateURL(activeTab, view);
    };

    const showModal = (type, title, message, onConfirm = null) => {
        setModal({
            isOpen: true,
            type,
            title,
            message,
            onConfirm
        });
    };

    const closeModal = () => {
        setModal({
            isOpen: false,
            type: 'info',
            title: '',
            message: '',
            onConfirm: null
        });
    };

    // 시간/분 input 스크롤 방지 및 값 변경
    useEffect(() => {
        const handleHourWheel = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const currentUpdate = bannerFormData.last_update || '';
            const timeMatch = currentUpdate.match(/(\d{2}):(\d{2})/);
            const dateMatch = currentUpdate.match(/^(\d{4})\.(\d{2})\.(\d{2})/);
            
            if (!dateMatch) return;
            
            let currentHour = timeMatch ? parseInt(timeMatch[1]) : 0;
            const minute = timeMatch ? timeMatch[2] : '00';
            
            // 스크롤 방향에 따라 증감
            if (e.deltaY < 0) {
                currentHour = Math.min(23, currentHour + 1);
            } else {
                currentHour = Math.max(0, currentHour - 1);
            }
            
            const paddedHour = currentHour.toString().padStart(2, '0');
            const newValue = `${dateMatch[1]}.${dateMatch[2]}.${dateMatch[3]} ${paddedHour}:${minute}`;
            setBannerFormData({...bannerFormData, last_update: newValue});
        };

        const handleMinuteWheel = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const currentUpdate = bannerFormData.last_update || '';
            const timeMatch = currentUpdate.match(/(\d{2}):(\d{2})/);
            const dateMatch = currentUpdate.match(/^(\d{4})\.(\d{2})\.(\d{2})/);
            
            if (!dateMatch) return;
            
            const hour = timeMatch ? timeMatch[1] : '00';
            let currentMinute = timeMatch ? parseInt(timeMatch[2]) : 0;
            
            // 스크롤 방향에 따라 5분 단위로 증감
            if (e.deltaY < 0) {
                currentMinute = Math.min(55, currentMinute + 5);
            } else {
                currentMinute = Math.max(0, currentMinute - 5);
            }
            
            const paddedMinute = currentMinute.toString().padStart(2, '0');
            const newValue = `${dateMatch[1]}.${dateMatch[2]}.${dateMatch[3]} ${hour}:${paddedMinute}`;
            setBannerFormData({...bannerFormData, last_update: newValue});
        };

        const hourInput = hourInputRef.current;
        const minuteInput = minuteInputRef.current;

        if (hourInput) {
            hourInput.addEventListener('wheel', handleHourWheel, { passive: false });
        }
        if (minuteInput) {
            minuteInput.addEventListener('wheel', handleMinuteWheel, { passive: false });
        }

        return () => {
            if (hourInput) {
                hourInput.removeEventListener('wheel', handleHourWheel);
            }
            if (minuteInput) {
                minuteInput.removeEventListener('wheel', handleMinuteWheel);
            }
        };
    }, [activeTab, bannerFormData]);

    const loadDeletedUsers = async () => {
        const response = await fetch('/api/admin/deleted-users', {
            headers: getAuthHeaders()
        });
        if (response.ok) {
            const data = await response.json();
            setDeletedUsers(data);
        }
    };

    const loadUsers = async () => {
        const response = await fetch('/api/admin/users', {
            headers: getAuthHeaders()
        });
        if (response.ok) {
            const data = await response.json();
            setUsers(data);
        }
    };

    const loadComments = async () => {
        const response = await fetch('/api/admin/comments', {
            headers: getAuthHeaders()
        });
        if (response.ok) {
            const data = await response.json();
            setComments(data);
        }
    };

    const loadStats = async () => {
        const response = await fetch('/api/admin/stats', {
            headers: getAuthHeaders()
        });
        if (response.ok) {
            const data = await response.json();
            setStats(data);
        }
    };

    const loadPageStats = async (period = 'week') => {
        const response = await fetch(`/api/admin/page-stats?period=${period}`, {
            headers: getAuthHeaders()
        });
        if (response.ok) {
            const data = await response.json();
            setPageStats(data.pageStats || []);
            setDailyStats(data.dailyStats || []);
        }
    };

    const loadMapLocations = async () => {
        const response = await fetch('/api/admin/map-locations', {
            headers: getAuthHeaders()
        });
        if (response.ok) {
            const data = await response.json();
            setMapLocations(data.locations || []);
            setMapLocationStats({
                userStats: data.userStats || [],
                mapStats: data.mapStats || [],
                totalLocations: data.totalLocations || 0
            });
        }
    };

    const loadBaitRecipes = async () => {
        const response = await fetch('/api/admin/bait-recipes', {
            headers: getAuthHeaders()
        });
        if (response.ok) {
            const data = await response.json();
            setBaitRecipes(data.recipes || []);
            setBaitRecipeStats(data.stats || {
                total_recipes: 0,
                unique_users: 0,
                last_created_date: null
            });
        }
    };

    const loadUserActivity = async (period = 'week', type = 'overview') => {
        const response = await fetch(`/api/admin/user-activity?period=${period}&type=${type}`, {
            headers: getAuthHeaders()
        });
        if (response.ok) {
            const data = await response.json();
            setUserActivity({
                userActivity: data.userActivity || [],
                ipActivity: data.ipActivity || [],
                pageActivity: data.pageActivity || [],
                dailyActivity: data.dailyActivity || [],
                summary: data.summary || {}
            });
        }
    };

    const loadBannerSettings = async () => {
        const response = await fetch('/api/admin/banner-settings', {
            headers: getAuthHeaders()
        });
        if (response.ok) {
            const data = await response.json();
            
            // 배너 설정을 폼 데이터로 변환
            const formData = {};
            data.forEach(setting => {
                formData[setting.setting_key] = setting.setting_value || '';
            });
            setBannerFormData(formData);
        }
    };

    const toggleSection = (section) => {
        setCollapsedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const updateDisplayBanner = async () => {
        try {
            const response = await fetch('/api/admin/banner-settings', {
                method: 'PUT',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    settings: {
                        banner_status: bannerFormData.banner_status
                    }
                })
            });

            if (response.ok) {
                showModal('success', '성공', '표시 배너가 변경되었습니다.');
                await loadBannerSettings();
            } else {
                const error = await response.json();
                showModal('error', '오류', error.error || '표시 배너 변경에 실패했습니다.');
            }
        } catch (error) {
            console.error('표시 배너 변경 오류:', error);
            showModal('error', '오류', '표시 배너 변경 중 오류가 발생했습니다.');
        }
    };

    const saveBannerSettings = async () => {
        try {
            const response = await fetch('/api/admin/banner-settings', {
                method: 'PUT',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ settings: bannerFormData })
            });

            if (response.ok) {
                showModal('success', '성공', '배너 설정이 저장되었습니다.');
                await loadBannerSettings();
            } else {
                const error = await response.json();
                showModal('error', '오류', error.error || '배너 설정 저장에 실패했습니다.');
            }
        } catch (error) {
            console.error('배너 설정 저장 오류:', error);
            showModal('error', '오류', '배너 설정 저장 중 오류가 발생했습니다.');
        }
    };

    const loadDashboardData = async () => {
        setLoading(true);
        setError('');
        
        try {
            // 먼저 관리자 권한 재확인
            const isAdminVerified = await verifyAdminAccess();
            
            if (!isAdminVerified) {
                setError('관리자 권한이 없거나 만료되었습니다.');
                setLoading(false);
                return;
            }

            // 사용자 목록 로드
            await loadUsers();
            // 삭제된 사용자 목록 로드
            await loadDeletedUsers();
            // 댓글 목록 로드
            await loadComments();
            // 통계 로드
            await loadStats();
            // 페이지 사용 통계 로드
            await loadPageStats(chartPeriod);
            // 지도 좌표 로드
            await loadMapLocations();
            // 밑밥 레시피 로드
            await loadBaitRecipes();
            // 사용자 활동 로드
            await loadUserActivity(activityPeriod, activityView);
            // 배너 설정 로드
            await loadBannerSettings();
        } catch (error) {
            console.error('대시보드 데이터 로드 오류:', error);
            setError('데이터를 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // 사용자가 로그인되어 있고 기본적인 관리자 권한이 있을 때 데이터 로드
        if (user && isAdmin()) {
            loadDashboardData();
        } else if (user && !isAdmin()) {
            // 사용자는 있지만 관리자가 아닌 경우
            setLoading(false);
            setError('관리자 권한이 필요합니다.');
        } else {
            // 사용자 정보가 없는 경우
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // URL 파라미터 변경 감지 (뒤로가기/앞으로가기 지원)
    useEffect(() => {
        const handlePopState = () => {
            const params = new URLSearchParams(window.location.search);
            const tab = params.get('tab') || 'users';
            const mapView = params.get('mapView') || 'overview';
            
            setActiveTab(tab);
            if (tab === 'map-locations') {
                setMapStatsView(mapView);
            }
        };

        window.addEventListener('popstate', handlePopState);
        
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    // 사용자 상태나 관리자 권한이 변경될 때마다 권한 재확인
    useEffect(() => {
        if (user && isAdmin()) {
            verifyAdminAccess();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleDeleteUser = async (userId) => {
        const confirmDelete = () => {
            deleteUser(userId);
        };

        showModal(
            'confirm',
            '사용자 삭제',
            '정말로 이 사용자를 삭제하시겠습니까?',
            confirmDelete
        );
    };

    const restoreUser = async (userId) => {
        try {
            const response = await fetch(`/api/admin/users/${userId}/restore`, {
                method: 'PUT',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                await loadDeletedUsers();
                await loadUsers();
                showModal('success', '성공', '사용자가 복구되었습니다.');
            } else {
                showModal('error', '오류', '사용자 복구에 실패했습니다.');
            }
        } catch (error) {
            console.error('사용자 복구 오류:', error);
            showModal('error', '오류', '오류가 발생했습니다.');
        }
    };

    const handleRestoreUser = async (userId) => {
        const confirmRestore = () => {
            restoreUser(userId);
        };

        showModal(
            'confirm',
            '사용자 복구',
            '정말로 이 사용자를 복구하시겠습니까?',
            confirmRestore
        );
    };

    const deleteUser = async (userId) => {
        try {
            const response = await fetch(`/api/admin/users/${userId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                await loadUsers();
                showModal('success', '성공', '사용자가 삭제되었습니다.');
            } else {
                showModal('error', '오류', '사용자 삭제에 실패했습니다.');
            }
        } catch (error) {
            console.error('사용자 삭제 오류:', error);
            showModal('error', '오류', '오류가 발생했습니다.');
        }
    };

    const handleDeleteComment = async (commentId) => {
        const confirmDelete = () => {
            deleteComment(commentId);
        };

        showModal(
            'confirm',
            '댓글 삭제',
            '정말로 이 댓글을 삭제하시겠습니까?',
            confirmDelete
        );
    };

    const deleteComment = async (commentId) => {
        try {
            const response = await fetch(`/api/admin/comments/${commentId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                await loadComments();
                showModal('success', '성공', '댓글이 삭제되었습니다.');
            } else {
                showModal('error', '오류', '댓글 삭제에 실패했습니다.');
            }
        } catch (error) {
            console.error('댓글 삭제 오류:', error);
            showModal('error', '오류', '오류가 발생했습니다.');
        }
    };

    const toggleUserAdmin = async (userId, currentStatus) => {
        try {
            const response = await fetch(`/api/admin/users/${userId}/admin`, {
                method: 'PUT',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ is_admin: !currentStatus })
            });

            if (response.ok) {
                await loadUsers();
                showModal('success', '성공', '사용자 권한이 변경되었습니다.');
            } else {
                showModal('error', '오류', '권한 변경에 실패했습니다.');
            }
        } catch (error) {
            console.error('권한 변경 오류:', error);
            showModal('error', '오류', '오류가 발생했습니다.');
        }
    };

    // 관리자 권한 확인
    if (!user) {
        return (
            <div className="admin-dashboard">
                <div className="admin-error">
                    <h2>로그인이 필요합니다</h2>
                    <p>관리자 대시보드에 접근하려면 로그인해 주세요.</p>
                </div>
            </div>
        );
    }

    if (!isAdmin()) {
        return (
            <div className="admin-dashboard">
                <div className="admin-error">
                    <h2>접근 권한이 없습니다</h2>
                    <p>관리자만 접근할 수 있는 페이지입니다.</p>
                </div>
            </div>
        );
    }

    if (!isAdminVerified && !loading && !adminVerifyLoading) {
        return (
            <div className="admin-dashboard">
                <div className="admin-error">
                    <h2>권한 확인 실패</h2>
                    <p>관리자 권한을 확인할 수 없습니다. 다시 로그인해 주세요.</p>
                    <div style={{ marginTop: '15px' }}>
                        <button 
                            onClick={async () => {
                                setLoading(true);
                                await refreshUserInfo();
                                await verifyAdminAccess();
                                await loadDashboardData();
                            }}
                            className="retry-button"
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                marginRight: '10px'
                            }}
                        >
                            사용자 정보 갱신
                        </button>
                        <button 
                            onClick={loadDashboardData}
                            className="retry-button"
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            다시 시도
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="admin-dashboard">
                <div className="admin-loading">
                    <h2>로딩 중...</h2>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <h1>관리자 대시보드</h1>
                <p>사이트 관리 및 사용자 관리</p>
            </div>

            {error && (
                <div className="admin-error">
                    <p>{error}</p>
                </div>
            )}

            <div className="admin-stats">
                <div className="stat-card">
                    <h3>총 사용자</h3>
                    <p className="stat-number">{stats.totalUsers || 0}</p>
                </div>
                <div className="stat-card">
                    <h3>삭제된 계정</h3>
                    <p className="stat-number">{stats.deletedUsers || 0}</p>
                </div>
                <div className="stat-card">
                    <h3>총 댓글</h3>
                    <p className="stat-number">{stats.totalComments || 0}</p>
                </div>
                <div className="stat-card">
                    <h3>오늘 가입</h3>
                    <p className="stat-number">{stats.todayUsers || 0}</p>
                </div>
                <div className="stat-card">
                    <h3>오늘 댓글</h3>
                    <p className="stat-number">{stats.todayComments || 0}</p>
                </div>
                <div className="stat-card">
                    <h3>오늘 삭제</h3>
                    <p className="stat-number">{stats.todayDeletedUsers || 0}</p>
                </div>
            </div>

            <div className="admin-tabs">
                <button 
                    className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => handleTabChange('users')}
                >
                    활성 사용자
                </button>
                <button 
                    className={`tab-button ${activeTab === 'deleted-users' ? 'active' : ''}`}
                    onClick={() => handleTabChange('deleted-users')}
                >
                    삭제된 계정
                </button>
                <button 
                    className={`tab-button ${activeTab === 'comments' ? 'active' : ''}`}
                    onClick={() => handleTabChange('comments')}
                >
                    댓글 관리
                </button>
                <button 
                    className={`tab-button ${activeTab === 'map-locations' ? 'active' : ''}`}
                    onClick={() => handleTabChange('map-locations')}
                >
                    지도 좌표
                </button>
                <button 
                    className={`tab-button ${activeTab === 'bait-recipes' ? 'active' : ''}`}
                    onClick={() => handleTabChange('bait-recipes')}
                >
                    밑밥 레시피
                </button>
                <button 
                    className={`tab-button ${activeTab === 'page-stats' ? 'active' : ''}`}
                    onClick={() => handleTabChange('page-stats')}
                >
                    페이지 통계
                </button>
                <button 
                    className={`tab-button ${activeTab === 'user-activity' ? 'active' : ''}`}
                    onClick={() => handleTabChange('user-activity')}
                >
                    사용자 활동
                </button>
                <button 
                    className={`tab-button ${activeTab === 'banner-settings' ? 'active' : ''}`}
                    onClick={() => handleTabChange('banner-settings')}
                >
                    배너 관리
                </button>
            </div>

            {activeTab === 'users' && (
                <div className="admin-content">
                    <h2>활성 사용자 목록</h2>
                    <div className="admin-table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>사용자명</th>
                                    <th>이메일</th>
                                    <th>관리자</th>
                                    <th>인증됨</th>
                                    <th>가입일</th>
                                    <th>작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(userItem => (
                                    <tr key={userItem.id}>
                                        <td>{userItem.id}</td>
                                        <td>{userItem.username}</td>
                                        <td>{userItem.email}</td>
                                        <td>
                                            <span className={`admin-badge ${userItem.is_admin ? 'admin' : 'user'}`}>
                                                {userItem.is_admin ? '관리자' : '사용자'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${userItem.is_verified ? 'verified' : 'unverified'}`}>
                                                {userItem.is_verified ? '인증됨' : '미인증'}
                                            </span>
                                        </td>
                                        <td>{new Date(userItem.created_at).toLocaleString('ko-KR', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}</td>
                                        <td>
                                            <div className="admin-actions">
                                                <button 
                                                    className="action-button admin-toggle"
                                                    onClick={() => toggleUserAdmin(userItem.id, userItem.is_admin)}
                                                    disabled={userItem.id === user.id}
                                                >
                                                    {userItem.is_admin ? '권한 해제' : '관리자 설정'}
                                                </button>
                                                <button 
                                                    className="action-button delete"
                                                    onClick={() => handleDeleteUser(userItem.id)}
                                                    disabled={userItem.id === user.id}
                                                >
                                                    삭제
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'deleted-users' && (
                <div className="admin-content">
                    <h2>삭제된 계정 목록</h2>
                    <p className="admin-description">
                        삭제된 계정들을 관리하고 필요시 복구할 수 있습니다.
                    </p>
                    <div className="admin-table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>사용자명</th>
                                    <th>이메일</th>
                                    <th>관리자 여부</th>
                                    <th>가입일</th>
                                    <th>삭제일</th>
                                    <th>삭제 후 경과일</th>
                                    <th>작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deletedUsers.map(userItem => (
                                    <tr key={userItem.id}>
                                        <td>{userItem.id}</td>
                                        <td>{userItem.username}</td>
                                        <td>{userItem.email}</td>
                                        <td>
                                            <span className={`admin-badge ${userItem.is_admin ? 'admin' : 'user'}`}>
                                                {userItem.is_admin ? '관리자' : '사용자'}
                                            </span>
                                        </td>
                                        <td>{new Date(userItem.created_at).toLocaleDateString()}</td>
                                        <td>{new Date(userItem.deleted_at).toLocaleString()}</td>
                                        <td>
                                            <span className="days-badge">
                                                {userItem.days_since_deletion}일
                                            </span>
                                        </td>
                                        <td>
                                            <div className="admin-actions">
                                                <button 
                                                    className="action-button restore"
                                                    onClick={() => handleRestoreUser(userItem.id)}
                                                >
                                                    복구
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {deletedUsers.length === 0 && (
                            <div className="no-data">
                                <p>삭제된 계정이 없습니다.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'comments' && (
                <div className="admin-content">
                    <h2>댓글 목록</h2>
                    <div className="admin-table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>상태</th>
                                    <th>페이지</th>
                                    <th>작성자</th>
                                    <th>내용</th>
                                    <th>작성일</th>
                                    <th>IP</th>
                                    <th>작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                {comments.map(comment => (
                                    <tr key={comment.id} className={comment.is_deleted ? 'deleted-comment' : ''}>
                                        <td>{comment.id}</td>
                                        <td>
                                            {comment.is_deleted ? (
                                                <span className="status-badge deleted" title="삭제된 댓글">
                                                    🗑️ 삭제됨
                                                </span>
                                            ) : (
                                                <span className="status-badge active" title="활성 댓글">
                                                    ✅ 활성
                                                </span>
                                            )}
                                        </td>
                                        <td>{comment.page_name}</td>
                                        <td>{comment.nickname}</td>
                                        <td className="comment-content">
                                            {comment.content.length > 100 
                                                ? `${comment.content.substring(0, 100)}...` 
                                                : comment.content
                                            }
                                        </td>
                                        <td>{new Date(comment.created_at).toLocaleString()}</td>
                                        <td>{comment.ip_address}</td>
                                        <td>
                                            {!comment.is_deleted ? (
                                                <button 
                                                    className="action-button delete"
                                                    onClick={() => handleDeleteComment(comment.id)}
                                                >
                                                    삭제
                                                </button>
                                            ) : (
                                                <span className="action-disabled" title="이미 삭제된 댓글">
                                                    삭제됨
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'map-locations' && (
                <div className="admin-content">
                    <h2>지도 좌표 관리</h2>
                    <p className="admin-description">
                        사용자들이 저장한 지도 좌표 정보를 확인할 수 있습니다. (총 {mapLocationStats.totalLocations}개)
                    </p>

                    {/* 통계 뷰 선택 버튼 */}
                    <div className="period-selector">
                        <div className="period-buttons">
                            <button 
                                className={`period-btn ${mapStatsView === 'overview' ? 'active' : ''}`}
                                onClick={() => handleMapViewChange('overview')}
                            >
                                통계 개요
                            </button>
                            <button 
                                className={`period-btn ${mapStatsView === 'users' ? 'active' : ''}`}
                                onClick={() => handleMapViewChange('users')}
                            >
                                사용자별 통계
                            </button>
                            <button 
                                className={`period-btn ${mapStatsView === 'maps' ? 'active' : ''}`}
                                onClick={() => handleMapViewChange('maps')}
                            >
                                맵별 통계
                            </button>
                            <button 
                                className={`period-btn ${mapStatsView === 'details' ? 'active' : ''}`}
                                onClick={() => handleMapViewChange('details')}
                            >
                                상세 목록
                            </button>
                        </div>
                    </div>

                    {/* 통계 개요 */}
                    {mapStatsView === 'overview' && (
                        <div className="map-stats-overview">
                            <div className="stats-grid">
                                <div className="stat-card">
                                    <h4>총 저장된 좌표</h4>
                                    <p className="stat-number">{mapLocationStats.totalLocations}</p>
                                </div>
                                <div className="stat-card">
                                    <h4>참여 사용자</h4>
                                    <p className="stat-number">{mapLocationStats.userStats.length}</p>
                                </div>
                                <div className="stat-card">
                                    <h4>사용된 맵</h4>
                                    <p className="stat-number">{mapLocationStats.mapStats.length}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 사용자별 통계 */}
                    {mapStatsView === 'users' && (
                        <div className="admin-table-container">
                            <h3>사용자별 좌표 저장 통계</h3>
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>사용자명</th>
                                        <th>저장한 좌표 수</th>
                                        <th>사용한 맵 수</th>
                                        <th>마지막 저장일</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mapLocationStats.userStats.map(user => (
                                        <tr key={user.user_id}>
                                            <td>{user.username}</td>
                                            <td>
                                                <span className="count-badge">
                                                    {user.location_count}개
                                                </span>
                                            </td>
                                            <td>
                                                <span className="count-badge secondary">
                                                    {user.unique_maps}개 맵
                                                </span>
                                            </td>
                                            <td>
                                                {new Date(user.last_saved).toLocaleString('ko-KR', {
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {mapLocationStats.userStats.length === 0 && (
                                <div className="no-data">
                                    <p>사용자별 통계 데이터가 없습니다.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 맵별 통계 */}
                    {mapStatsView === 'maps' && (
                        <div className="admin-table-container">
                            <h3>맵별 좌표 저장 통계</h3>
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>맵 이름</th>
                                        <th>저장된 좌표 수</th>
                                        <th>참여 사용자 수</th>
                                        <th>마지막 저장일</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mapLocationStats.mapStats.map(map => (
                                        <tr key={map.map_name}>
                                            <td>{getKoreanMapName(map.map_name)}</td>
                                            <td>
                                                <span className="count-badge">
                                                    {map.location_count}개
                                                </span>
                                            </td>
                                            <td>
                                                <span className="count-badge secondary">
                                                    {map.unique_users}명
                                                </span>
                                            </td>
                                            <td>
                                                {new Date(map.last_saved).toLocaleString('ko-KR', {
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {mapLocationStats.mapStats.length === 0 && (
                                <div className="no-data">
                                    <p>맵별 통계 데이터가 없습니다.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 상세 목록 */}
                    {mapStatsView === 'details' && (
                        <div className="admin-table-container">
                            <h3>저장된 좌표 상세 목록 (최근 100개)</h3>
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>사용자명</th>
                                        <th>지도명</th>
                                        <th>X좌표</th>
                                        <th>Y좌표</th>
                                        <th>낚시유형</th>
                                        <th>어종</th>
                                        <th>미끼</th>
                                        <th>메모</th>
                                        <th>저장일</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mapLocations.slice(0, 100).map(location => (
                                        <tr key={location.id}>
                                            <td>{location.id}</td>
                                            <td>{location.username || '-'}</td>
                                            <td>{getKoreanMapName(location.map_name)}</td>
                                            <td>{location.coord_x}</td>
                                            <td>{location.coord_y}</td>
                                            <td>
                                                <span className={`type-badge ${location.type}`}>
                                                    {location.type === 'float' ? '찌낚시' : 
                                                     location.type === 'bottom' ? '바닥낚시' : 
                                                     location.type === 'lure' ? '루어낚시' : location.type}
                                                </span>
                                            </td>
                                            <td>{location.fish || '-'}</td>
                                            <td>{location.bait || '-'}</td>
                                            <td className="memo-cell">
                                                {location.memo ? 
                                                    (location.memo.length > 30 ? 
                                                        `${location.memo.substring(0, 30)}...` : 
                                                        location.memo
                                                    ) : '-'
                                                }
                                            </td>
                                            <td>{new Date(location.created_at).toLocaleString('ko-KR', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {mapLocations.length === 0 ? (
                                <div className="no-data">
                                    <p>저장된 지도 좌표가 없습니다.</p>
                                </div>
                            ) : mapLocations.length > 100 && (
                                <div className="no-data">
                                    <p style={{ color: '#ff6b6b', fontWeight: 'bold' }}>
                                        총 {mapLocations.length}개 중 최근 100개만 표시됩니다.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'bait-recipes' && (
                <div className="admin-content">
                    <h2>밑밥 레시피 관리</h2>
                    <p className="admin-description">
                        사용자들이 저장한 밑밥 레시피를 확인할 수 있습니다.
                    </p>

                    {/* 통계 카드 */}
                    <div className="stats-grid" style={{ marginBottom: '20px' }}>
                        <div className="stat-card">
                            <h4>총 레시피 수</h4>
                            <p className="stat-number">{baitRecipeStats.total_recipes}</p>
                        </div>
                        <div className="stat-card">
                            <h4>참여 사용자</h4>
                            <p className="stat-number">{baitRecipeStats.unique_users}</p>
                        </div>
                        <div className="stat-card">
                            <h4>마지막 생성일</h4>
                            <p className="stat-number" style={{ fontSize: '16px' }}>
                                {baitRecipeStats.last_created_date ? 
                                    new Date(baitRecipeStats.last_created_date).toLocaleDateString('ko-KR') : 
                                    '-'
                                }
                            </p>
                        </div>
                    </div>

                    {/* 레시피 테이블 */}
                    <div className="admin-table-container">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>생성시간</th>
                                    <th>아이디</th>
                                    <th>저장된 이름</th>
                                    <th>베이스</th>
                                    <th>첨가제1</th>
                                    <th>첨가제2</th>
                                    <th>첨가제3</th>
                                    <th>첨가제4</th>
                                    <th>유인제</th>
                                </tr>
                            </thead>
                            <tbody>
                                {baitRecipes.map(recipe => (
                                    <tr key={recipe.id}>
                                        <td>
                                            {new Date(recipe.created_at).toLocaleString('ko-KR', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </td>
                                        <td>
                                            <span className="user-badge logged">
                                                {recipe.username || '알 수 없음'}
                                            </span>
                                        </td>
                                        <td className="recipe-name-cell">
                                            {recipe.saved_name}
                                        </td>
                                        <td>{recipe.base_name || '-'}</td>
                                        <td>{recipe.additive1_name || '-'}</td>
                                        <td>{recipe.additive2_name || '-'}</td>
                                        <td>{recipe.additive3_name || '-'}</td>
                                        <td>{recipe.additive4_name || '-'}</td>
                                        <td>{recipe.attractant_name || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {baitRecipes.length === 0 && (
                            <div className="no-data">
                                <p>저장된 밑밥 레시피가 없습니다.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'page-stats' && (
                <div className="admin-content">
                    <h2>페이지 사용 통계</h2>
                    
                    {/* 기간 선택 버튼 */}
                    <div className="period-selector">
                        <h3>일자별 조회수 추이</h3>
                        <div className="period-buttons">
                            <button 
                                className={`period-btn ${chartPeriod === 'day' ? 'active' : ''}`}
                                onClick={async () => {
                                    setChartPeriod('day');
                                    await loadPageStats('day');
                                }}
                            >
                                오늘
                            </button>
                            <button 
                                className={`period-btn ${chartPeriod === 'week' ? 'active' : ''}`}
                                onClick={async () => {
                                    setChartPeriod('week');
                                    await loadPageStats('week');
                                }}
                            >
                                일주일
                            </button>
                            <button 
                                className={`period-btn ${chartPeriod === 'month' ? 'active' : ''}`}
                                onClick={async () => {
                                    setChartPeriod('month');
                                    await loadPageStats('month');
                                }}
                            >
                                30일
                            </button>
                        </div>
                    </div>

                    {/* 선그래프 */}
                    <div className="line-chart-container">
                        {dailyStats.length > 0 ? (
                            <div className="line-chart">
                                <svg width="100%" height="300" viewBox="0 0 800 300">
                                    <defs>
                                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                            <stop offset="0%" stopColor="#007bff" stopOpacity="0.8"/>
                                            <stop offset="100%" stopColor="#007bff" stopOpacity="0.1"/>
                                        </linearGradient>
                                    </defs>
                                    
                                    {/* 그리드 라인 */}
                                    <g className="grid">
                                        {[0, 1, 2, 3, 4].map(i => (
                                            <line 
                                                key={i}
                                                x1="60" 
                                                y1={50 + i * 50} 
                                                x2="740" 
                                                y2={50 + i * 50}
                                                stroke="#e0e0e0"
                                                strokeWidth="1"
                                            />
                                        ))}
                                    </g>
                                    
                                    {/* 데이터 라인과 포인트 */}
                                    {(() => {
                                        const maxViews = Math.max(...dailyStats.map(d => d.total_views), 1);
                                        const points = dailyStats.map((stat, index) => ({
                                            x: 60 + (index * (680 / Math.max(dailyStats.length - 1, 1))),
                                            y: 250 - ((stat.total_views / maxViews) * 200)
                                        }));
                                        
                                        const pathData = points.reduce((path, point, index) => {
                                            return path + (index === 0 ? `M ${point.x} ${point.y}` : ` L ${point.x} ${point.y}`);
                                        }, '');
                                        
                                        const areaData = `${pathData} L ${points[points.length - 1]?.x || 60} 250 L 60 250 Z`;
                                        
                                        return (
                                            <g>
                                                {/* 영역 채우기 */}
                                                <path
                                                    d={areaData}
                                                    fill="url(#lineGradient)"
                                                />
                                                
                                                {/* 라인 */}
                                                <path
                                                    d={pathData}
                                                    fill="none"
                                                    stroke="#007bff"
                                                    strokeWidth="3"
                                                />
                                                
                                                {/* 포인트 */}
                                                {points.map((point, index) => (
                                                    <g key={index}>
                                                        <circle
                                                            cx={point.x}
                                                            cy={point.y}
                                                            r="4"
                                                            fill="#007bff"
                                                            stroke="white"
                                                            strokeWidth="2"
                                                        />
                                                        {/* 날짜 라벨 */}
                                                        <text
                                                            x={point.x}
                                                            y="280"
                                                            textAnchor="middle"
                                                            fontSize="12"
                                                            fill="#666"
                                                        >
                                                            {new Date(dailyStats[index].date).toLocaleDateString('ko-KR', {
                                                                month: 'short',
                                                                day: 'numeric'
                                                            })}
                                                        </text>
                                                        {/* 값 라벨 */}
                                                        <text
                                                            x={point.x}
                                                            y={point.y - 10}
                                                            textAnchor="middle"
                                                            fontSize="12"
                                                            fill="#007bff"
                                                            fontWeight="bold"
                                                        >
                                                            {dailyStats[index].total_views}
                                                        </text>
                                                    </g>
                                                ))}
                                                
                                                {/* Y축 라벨 */}
                                                {[0, 1, 2, 3, 4].map(i => (
                                                    <text
                                                        key={i}
                                                        x="50"
                                                        y={255 - i * 50}
                                                        textAnchor="end"
                                                        fontSize="12"
                                                        fill="#666"
                                                    >
                                                        {Math.round((maxViews / 4) * i)}
                                                    </text>
                                                ))}
                                            </g>
                                        );
                                    })()}
                                    
                                    {/* 축 */}
                                    <line x1="60" y1="250" x2="740" y2="250" stroke="#333" strokeWidth="2"/>
                                    <line x1="60" y1="50" x2="60" y2="250" stroke="#333" strokeWidth="2"/>
                                </svg>
                            </div>
                        ) : (
                            <div className="no-chart-data">
                                <p>선택한 기간의 데이터가 없습니다.</p>
                            </div>
                        )}
                    </div>

                    <div className="page-stats-container">
                        <div className="stats-table">
                            <h3>페이지별 상세 통계</h3>
                            <div className="admin-table-container">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>페이지명</th>
                                            <th>총 조회수</th>
                                            <th>30일</th>
                                            <th>일주일</th>
                                            <th>오늘</th>
                                            <th>마지막 접근</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageStats.map((stat, index) => (
                                            <tr key={index}>
                                                <td>{stat.page_name}</td>
                                                <td>
                                                    <span className="view-count-badge">
                                                        {stat.total_views.toLocaleString()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="month-count-badge">
                                                        {(stat.month_views || 0).toLocaleString()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="week-count-badge">
                                                        {(stat.week_views || 0).toLocaleString()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="today-count-badge">
                                                        {(stat.today_views || 0).toLocaleString()}
                                                    </span>
                                                </td>
                                                <td>
                                                    {stat.last_access ? 
                                                        new Date(stat.last_access).toLocaleString('ko-KR') : 
                                                        '-'
                                                    }
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'user-activity' && (
                <div className="admin-content">
                    <h2>사용자 활동 분석</h2>
                    <p className="admin-description">
                        사용자별/IP별 페이지 이용 현황을 기간별로 분석할 수 있습니다.
                    </p>

                    {/* 기간 및 뷰 선택 */}
                    <div className="period-selector">
                        <div className="period-section">
                            <h4>기간 선택</h4>
                            <div className="period-buttons">
                                <button 
                                    className={`period-btn ${activityPeriod === 'day' ? 'active' : ''}`}
                                    onClick={async () => {
                                        setActivityPeriod('day');
                                        await loadUserActivity('day', activityView);
                                    }}
                                >
                                    오늘
                                </button>
                                <button 
                                    className={`period-btn ${activityPeriod === 'week' ? 'active' : ''}`}
                                    onClick={async () => {
                                        setActivityPeriod('week');
                                        await loadUserActivity('week', activityView);
                                    }}
                                >
                                    일주일
                                </button>
                                <button 
                                    className={`period-btn ${activityPeriod === 'month' ? 'active' : ''}`}
                                    onClick={async () => {
                                        setActivityPeriod('month');
                                        await loadUserActivity('month', activityView);
                                    }}
                                >
                                    30일
                                </button>
                                <button 
                                    className={`period-btn ${activityPeriod === 'all' ? 'active' : ''}`}
                                    onClick={async () => {
                                        setActivityPeriod('all');
                                        await loadUserActivity('all', activityView);
                                    }}
                                >
                                    전체
                                </button>
                            </div>
                        </div>

                        <div className="period-section">
                            <h4>분석 유형</h4>
                            <div className="period-buttons">
                                <button 
                                    className={`period-btn ${activityView === 'overview' ? 'active' : ''}`}
                                    onClick={async () => {
                                        setActivityView('overview');
                                        await loadUserActivity(activityPeriod, 'overview');
                                    }}
                                >
                                    전체 개요
                                </button>
                                <button 
                                    className={`period-btn ${activityView === 'users' ? 'active' : ''}`}
                                    onClick={async () => {
                                        setActivityView('users');
                                        await loadUserActivity(activityPeriod, 'users');
                                    }}
                                >
                                    사용자별
                                </button>
                                <button 
                                    className={`period-btn ${activityView === 'ips' ? 'active' : ''}`}
                                    onClick={async () => {
                                        setActivityView('ips');
                                        await loadUserActivity(activityPeriod, 'ips');
                                    }}
                                >
                                    IP별
                                </button>
                                <button 
                                    className={`period-btn ${activityView === 'pages' ? 'active' : ''}`}
                                    onClick={async () => {
                                        setActivityView('pages');
                                        await loadUserActivity(activityPeriod, 'pages');
                                    }}
                                >
                                    페이지별
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* 요약 통계 */}
                    <div className="activity-summary">
                        <div className="stats-grid">
                            <div className="stat-card">
                                <h4>총 방문수</h4>
                                <p className="stat-number">{userActivity.summary.total_visits || 0}</p>
                            </div>
                            <div className="stat-card">
                                <h4>고유 사용자</h4>
                                <p className="stat-number">{userActivity.summary.total_unique_users || 0}</p>
                            </div>
                            <div className="stat-card">
                                <h4>고유 IP</h4>
                                <p className="stat-number">{userActivity.summary.total_unique_ips || 0}</p>
                            </div>
                            <div className="stat-card">
                                <h4>방문 페이지</h4>
                                <p className="stat-number">{userActivity.summary.total_unique_pages || 0}</p>
                            </div>
                        </div>
                    </div>

                    {/* 사용자별 활동 */}
                    {(activityView === 'overview' || activityView === 'users') && userActivity.userActivity && (
                        <div className="activity-section">
                            <h3>사용자별 활동 현황</h3>
                            <div className="admin-table-container">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>사용자</th>
                                            <th>IP 주소</th>
                                            <th>총 방문수</th>
                                            <th>방문 페이지 수</th>
                                            <th>활동 일수</th>
                                            <th>첫 방문</th>
                                            <th>최근 방문</th>
                                            <th>최근 방문 페이지</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {userActivity.userActivity.map((activity, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <span className={`user-badge ${activity.username === 'anonymous' ? 'anonymous' : 'logged'}`}>
                                                        {activity.username}
                                                    </span>
                                                </td>
                                                <td className="ip-address">{activity.ip_address}</td>
                                                <td>
                                                    <span className="count-badge">
                                                        {activity.total_visits}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="count-badge secondary">
                                                        {activity.unique_pages}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="count-badge tertiary">
                                                        {activity.active_days}일
                                                    </span>
                                                </td>
                                                <td>
                                                    {new Date(activity.first_visit).toLocaleString('ko-KR', {
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </td>
                                                <td>
                                                    {new Date(activity.last_visit).toLocaleString('ko-KR', {
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </td>
                                                <td className="visited-pages">
                                                    {activity.last_visited_page ? getPageLabel(activity.last_visited_page) : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {userActivity.userActivity.length === 0 && (
                                    <div className="no-data">
                                        <p>선택한 기간의 사용자 활동 데이터가 없습니다.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* IP별 활동 */}
                    {(activityView === 'overview' || activityView === 'ips') && userActivity.ipActivity && (
                        <div className="activity-section">
                            <h3>IP별 활동 현황</h3>
                            <div className="admin-table-container">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>IP 주소</th>
                                            <th>사용자 수</th>
                                            <th>총 방문수</th>
                                            <th>방문 페이지 수</th>
                                            <th>활동 일수</th>
                                            <th>첫 방문</th>
                                            <th>최근 방문</th>
                                            <th>최근 방문 페이지</th>
                                            <th>사용자명</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {userActivity.ipActivity.map((activity, index) => (
                                            <tr key={index}>
                                                <td className="ip-address">{activity.ip_address}</td>
                                                <td>
                                                    <span className="count-badge">
                                                        {activity.unique_users}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="count-badge secondary">
                                                        {activity.total_visits}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="count-badge tertiary">
                                                        {activity.unique_pages}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="count-badge">
                                                        {activity.active_days}일
                                                    </span>
                                                </td>
                                                <td>
                                                    {new Date(activity.first_visit).toLocaleString('ko-KR', {
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </td>
                                                <td>
                                                    {new Date(activity.last_visit).toLocaleString('ko-KR', {
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </td>
                                                <td className="visited-pages">
                                                    {activity.last_visited_page ? getPageLabel(activity.last_visited_page) : '-'}
                                                </td>
                                                <td className="usernames">
                                                    {(() => {
                                                        if (!activity.usernames) return '-';
                                                        const filteredUsernames = activity.usernames
                                                            .split(', ')
                                                            .filter(name => name.trim() !== 'anonymous')
                                                            .join(', ');
                                                        if (!filteredUsernames) return '-';
                                                        return filteredUsernames.length > 30 ? 
                                                            `${filteredUsernames.substring(0, 30)}...` : 
                                                            filteredUsernames;
                                                    })()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {userActivity.ipActivity.length === 0 && (
                                    <div className="no-data">
                                        <p>선택한 기간의 IP 활동 데이터가 없습니다.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 페이지별 활동 */}
                    {(activityView === 'overview' || activityView === 'pages') && userActivity.pageActivity && (
                        <div className="activity-section">
                            <h3>페이지별 활동 현황</h3>
                            <div className="admin-table-container">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>페이지명</th>
                                            <th>총 방문수</th>
                                            <th>고유 사용자</th>
                                            <th>고유 IP</th>
                                            <th>활동 일수</th>
                                            <th>첫 방문</th>
                                            <th>최근 방문</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {userActivity.pageActivity.map((activity, index) => (
                                            <tr key={index}>
                                                <td className="page-name">{getPageLabel(activity.page_name)}</td>
                                                <td>
                                                    <span className="count-badge">
                                                        {activity.total_visits}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="count-badge secondary">
                                                        {activity.unique_users}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="count-badge tertiary">
                                                        {activity.unique_ips}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="count-badge">
                                                        {activity.active_days}일
                                                    </span>
                                                </td>
                                                <td>
                                                    {new Date(activity.first_visit).toLocaleString('ko-KR', {
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </td>
                                                <td>
                                                    {new Date(activity.last_visit).toLocaleString('ko-KR', {
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {userActivity.pageActivity.length === 0 && (
                                    <div className="no-data">
                                        <p>선택한 기간의 페이지 활동 데이터가 없습니다.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'banner-settings' && (
                <div className="admin-content">
                    <h2>배너 관리</h2>
                    <p className="admin-description">
                        메인 페이지 배너 설정을 관리합니다. 배너 상태에 따라 다른 제목과 내용이 표시됩니다.
                    </p>

                    <div className="banner-settings-container">
                        {/* 표시 배너 설정 */}
                        <div className="settings-section">
                            <h3>📌 표시 배너 설정</h3>
                            <div className="form-group">
                                <label htmlFor="banner_status">현재 표시할 배너:</label>
                                <select
                                    id="banner_status"
                                    value={bannerFormData.banner_status || 'welcome'}
                                    onChange={(e) => setBannerFormData({...bannerFormData, banner_status: e.target.value})}
                                    className="form-control"
                                >
                                    <option value="welcome">환영 배너 (Welcome)</option>
                                    <option value="notice">공지 배너 (Notice)</option>
                                    <option value="event">이벤트 배너 (Event)</option>
                                    <option value="maintenance">점검 배너 (Maintenance)</option>
                                </select>
                                <small className="form-help">
                                    선택한 배너가 메인 페이지에 표시됩니다.
                                </small>
                            </div>
                            
                            {/* 배너 미리보기 */}
                            <div className="banner-preview-container" style={{ marginTop: '20px' }}>
                                <h4 style={{ color: 'var(--text-color)', marginBottom: '10px', fontSize: '14px' }}>
                                    📺 배너 미리보기
                                </h4>
                                <div className={`banner banner-${bannerFormData.banner_status || 'welcome'} banner-preview`} style={{ 
                                    maxWidth: '100%',
                                    margin: '0'
                                }}>
                                    <img 
                                        src={(() => {
                                            const status = bannerFormData.banner_status || 'welcome';
                                            const imageKey = status === 'welcome' ? 'banner_default_image' : `banner_${status}_image`;
                                            return bannerFormData[imageKey] || bannerFormData.banner_image || '/banner.webp';
                                        })()}
                                        alt="Banner Preview" 
                                        className="banner-image" 
                                    />
                                    <div className="banner-text">
                                        <h1>
                                            {(() => {
                                                const status = bannerFormData.banner_status || 'welcome';
                                                const titleKey = status === 'welcome' ? 'banner_default_title' : `banner_${status}_title`;
                                                return bannerFormData[titleKey] || '제목 없음';
                                            })()}
                                        </h1>
                                        {(() => {
                                            const status = bannerFormData.banner_status || 'welcome';
                                            const contentKey = status === 'welcome' ? 'banner_default_content' : `banner_${status}_content`;
                                            let content = bannerFormData[contentKey] || '';
                                            // \n 문자열을 실제 줄바꿈으로 변환
                                            content = content.replace(/\\n/g, '\n');
                                            const contentLines = content.split('\n');
                                            return contentLines.map((line, index) => (
                                                <p key={index}>{line}</p>
                                            ));
                                        })()}
                                        <p><strong>관리자 : {bannerFormData.admin_name || 'min5162'}</strong></p>
                                        <p><strong>이메일 : {bannerFormData.admin_email || 'rfkrdatasite@gmail.com'}</strong></p>
                                        <p><strong>업데이트: {bannerFormData.last_update || '2025.08.16'}</strong></p>
                                        
                                        {/* 배너 버튼 미리보기 */}
                                        {(bannerFormData.banner_button_display === 'true' || bannerFormData.banner_button_display === true) && bannerFormData.banner_button_link && (
                                            <div className="banner-button" style={{ display: 'inline-block', pointerEvents: 'none' }}>
                                                {bannerFormData.banner_button_icon && (
                                                    <span style={{ marginRight: '5px' }}>{bannerFormData.banner_button_icon}</span>
                                                )}
                                                <span className="banner-button-text">{bannerFormData.banner_button_text || '배너 버튼'}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <div style={{ marginTop: '15px', textAlign: 'center' }}>
                                <button 
                                    className="btn-update-display-banner"
                                    onClick={updateDisplayBanner}
                                    style={{
                                        padding: '10px 25px',
                                        fontSize: '15px',
                                        fontWeight: 'bold',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    🔄 표시 배너 변경
                                </button>
                            </div>
                        </div>

                        {/* 배너 설정 (접을 수 있음) */}
                        <div className="settings-section collapsible">
                            <div 
                                className="section-header"
                                onClick={() => toggleSection('banner')}
                                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            >
                                <h3 style={{ margin: 0 }}>🎨 배너 설정</h3>
                                <span style={{ fontSize: '1.5em' }}>
                                    {collapsedSections.banner ? '▼' : '▲'}
                                </span>
                            </div>
                            
                            {!collapsedSections.banner && (
                                <>
                                    {/* 설정할 배너 종류 선택 */}
                                    <div className="form-group" style={{ marginTop: '20px' }}>
                                        <label htmlFor="selected_banner_type">설정할 배너 종류:</label>
                                        <select
                                            id="selected_banner_type"
                                            value={selectedBannerType}
                                            onChange={(e) => setSelectedBannerType(e.target.value)}
                                            className="form-control"
                                        >
                                            <option value="default">🎉 환영 배너 (Welcome)</option>
                                            <option value="notice">📢 공지 배너 (Notice)</option>
                                            <option value="event">🎊 이벤트 배너 (Event)</option>
                                            <option value="maintenance">🔧 점검 배너 (Maintenance)</option>
                                        </select>
                                    </div>

                                    {/* 선택된 배너 타입에 따른 설정 폼 */}
                                    <div className="banner-type-settings">
                                        <div className="form-group">
                                            <label htmlFor={`banner_${selectedBannerType}_title`}>제목:</label>
                                            <input
                                                type="text"
                                                id={`banner_${selectedBannerType}_title`}
                                                value={bannerFormData[`banner_${selectedBannerType}_title`] || ''}
                                                onChange={(e) => setBannerFormData({
                                                    ...bannerFormData, 
                                                    [`banner_${selectedBannerType}_title`]: e.target.value
                                                })}
                                                className="form-control"
                                                placeholder={
                                                    selectedBannerType === 'default' ? '데이터 사이트에 오신 것을 환영합니다' :
                                                    selectedBannerType === 'notice' ? '중요 공지사항' :
                                                    selectedBannerType === 'event' ? '이벤트 진행중!' :
                                                    '시스템 점검 안내'
                                                }
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor={`banner_${selectedBannerType}_content`}>내용:</label>
                                            <textarea
                                                id={`banner_${selectedBannerType}_content`}
                                                value={bannerFormData[`banner_${selectedBannerType}_content`] || ''}
                                                onChange={(e) => setBannerFormData({
                                                    ...bannerFormData, 
                                                    [`banner_${selectedBannerType}_content`]: e.target.value
                                                })}
                                                className="form-control"
                                                rows="6"
                                                placeholder="배너 내용을 입력하세요"
                                            />
                                            <small className="form-help">
                                                줄바꿈은 \n을 사용하세요
                                            </small>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor={`banner_${selectedBannerType}_image`}>이미지 경로:</label>
                                            <input
                                                type="text"
                                                id={`banner_${selectedBannerType}_image`}
                                                value={bannerFormData[`banner_${selectedBannerType}_image`] || ''}
                                                onChange={(e) => setBannerFormData({
                                                    ...bannerFormData, 
                                                    [`banner_${selectedBannerType}_image`]: e.target.value
                                                })}
                                                className="form-control"
                                                placeholder={selectedBannerType === 'maintenance' ? '/maitenance.webp' : '/banner.webp'}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* 배너 버튼 설정 (접을 수 있음) */}
                        <div className="settings-section collapsible">
                            <div 
                                className="section-header"
                                onClick={() => toggleSection('button')}
                                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            >
                                <h3 style={{ margin: 0 }}>🔘 배너 버튼 설정</h3>
                                <span style={{ fontSize: '1.5em' }}>
                                    {collapsedSections.button ? '▼' : '▲'}
                                </span>
                            </div>
                            
                            {!collapsedSections.button && (
                                <div style={{ marginTop: '20px' }}>
                                    <div className="form-group">
                                        <label htmlFor="banner_button_display">버튼 표시:</label>
                                        <select
                                            id="banner_button_display"
                                            value={bannerFormData.banner_button_display || 'false'}
                                            onChange={(e) => setBannerFormData({...bannerFormData, banner_button_display: e.target.value})}
                                            className="form-control"
                                        >
                                            <option value="false">숨김</option>
                                            <option value="true">표시</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="banner_button_text">버튼 텍스트:</label>
                                        <input
                                            type="text"
                                            id="banner_button_text"
                                            value={bannerFormData.banner_button_text || ''}
                                            onChange={(e) => setBannerFormData({...bannerFormData, banner_button_text: e.target.value})}
                                            className="form-control"
                                            placeholder="배너 버튼"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="banner_button_link">버튼 링크:</label>
                                        <input
                                            type="text"
                                            id="banner_button_link"
                                            value={bannerFormData.banner_button_link || ''}
                                            onChange={(e) => setBannerFormData({...bannerFormData, banner_button_link: e.target.value})}
                                            className="form-control"
                                            placeholder="https://example.com"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="banner_button_icon">버튼 아이콘:</label>
                                        <input
                                            type="text"
                                            id="banner_button_icon"
                                            value={bannerFormData.banner_button_icon || ''}
                                            onChange={(e) => setBannerFormData({...bannerFormData, banner_button_icon: e.target.value})}
                                            className="form-control"
                                            placeholder="🔗"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="banner_button_target">링크 열기 방식:</label>
                                        <select
                                            id="banner_button_target"
                                            value={bannerFormData.banner_button_target || '_blank'}
                                            onChange={(e) => setBannerFormData({...bannerFormData, banner_button_target: e.target.value})}
                                            className="form-control"
                                        >
                                            <option value="_blank">새 탭</option>
                                            <option value="_self">현재 탭</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 기타 설정 (접을 수 있음) */}
                        <div className="settings-section collapsible">
                            <div 
                                className="section-header"
                                onClick={() => toggleSection('other')}
                                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            >
                                <h3 style={{ margin: 0 }}>⚙️ 기타 설정</h3>
                                <span style={{ fontSize: '1.5em' }}>
                                    {collapsedSections.other ? '▼' : '▲'}
                                </span>
                            </div>
                            
                            {!collapsedSections.other && (
                                <div style={{ marginTop: '20px' }}>
                                    <div className="form-group">
                                        <label htmlFor="admin_name">관리자 이름:</label>
                                        <input
                                            type="text"
                                            id="admin_name"
                                            value={bannerFormData.admin_name || ''}
                                            onChange={(e) => setBannerFormData({...bannerFormData, admin_name: e.target.value})}
                                            className="form-control"
                                            placeholder="min5162"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="admin_email">관리자 이메일:</label>
                                        <input
                                            type="email"
                                            id="admin_email"
                                            value={bannerFormData.admin_email || ''}
                                            onChange={(e) => setBannerFormData({...bannerFormData, admin_email: e.target.value})}
                                            className="form-control"
                                            placeholder="rfkrdatasite@gmail.com"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="last_update">마지막 업데이트:</label>
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                            <input
                                                type="date"
                                                id="last_update_date"
                                                value={(() => {
                                                    const dateStr = bannerFormData.last_update || '';
                                                    if (!dateStr) return '';
                                                    const match = dateStr.match(/^(\d{4})\.(\d{2})\.(\d{2})/);
                                                    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
                                                    return '';
                                                })()}
                                                onChange={(e) => {
                                                    const dateValue = e.target.value; // YYYY-MM-DD
                                                    const currentUpdate = bannerFormData.last_update || '';
                                                    const timeMatch = currentUpdate.match(/(\d{2}):(\d{2})/);
                                                    const hour = timeMatch ? timeMatch[1] : '';
                                                    const minute = timeMatch ? timeMatch[2] : '';
                                                    
                                                    if (dateValue) {
                                                        const [year, month, day] = dateValue.split('-');
                                                        let newValue = `${year}.${month}.${day}`;
                                                        if (hour && minute) {
                                                            newValue += ` ${hour}:${minute}`;
                                                        }
                                                        setBannerFormData({...bannerFormData, last_update: newValue});
                                                    } else {
                                                        setBannerFormData({...bannerFormData, last_update: ''});
                                                    }
                                                }}
                                                className="form-control"
                                                style={{ flex: '2' }}
                                            />
                                            <input
                                                type="number"
                                                id="last_update_hour"
                                                ref={hourInputRef}
                                                min="0"
                                                max="23"
                                                placeholder="시"
                                                value={(() => {
                                                    const dateStr = bannerFormData.last_update || '';
                                                    const match = dateStr.match(/(\d{2}):(\d{2})/);
                                                    return match ? match[1] : '';
                                                })()}
                                                onChange={(e) => {
                                                    let hour = e.target.value;
                                                    if (hour && (parseInt(hour) > 23 || parseInt(hour) < 0)) return;
                                                    
                                                    const currentUpdate = bannerFormData.last_update || '';
                                                    const dateMatch = currentUpdate.match(/^(\d{4})\.(\d{2})\.(\d{2})/);
                                                    const timeMatch = currentUpdate.match(/(\d{2}):(\d{2})/);
                                                    const minute = timeMatch ? timeMatch[2] : '';
                                                    
                                                    if (dateMatch) {
                                                        let newValue = `${dateMatch[1]}.${dateMatch[2]}.${dateMatch[3]}`;
                                                        if (hour && minute) {
                                                            hour = hour.padStart(2, '0');
                                                            newValue += ` ${hour}:${minute}`;
                                                        }
                                                        setBannerFormData({...bannerFormData, last_update: newValue});
                                                    }
                                                }}
                                                className="form-control"
                                                style={{ flex: '0.5', textAlign: 'center' }}
                                            />
                                            <span>:</span>
                                            <input
                                                type="number"
                                                id="last_update_minute"
                                                ref={minuteInputRef}
                                                min="0"
                                                max="55"
                                                step="5"
                                                placeholder="분"
                                                value={(() => {
                                                    const dateStr = bannerFormData.last_update || '';
                                                    const match = dateStr.match(/(\d{2}):(\d{2})/);
                                                    return match ? match[2] : '';
                                                })()}
                                                onChange={(e) => {
                                                    let minute = e.target.value;
                                                    if (minute && (parseInt(minute) > 59 || parseInt(minute) < 0)) return;
                                                    
                                                    // 5분 단위로 반올림
                                                    if (minute) {
                                                        minute = Math.round(parseInt(minute) / 5) * 5;
                                                        if (minute > 55) minute = 55;
                                                    }
                                                    
                                                    const currentUpdate = bannerFormData.last_update || '';
                                                    const dateMatch = currentUpdate.match(/^(\d{4})\.(\d{2})\.(\d{2})/);
                                                    const timeMatch = currentUpdate.match(/(\d{2}):(\d{2})/);
                                                    const hour = timeMatch ? timeMatch[1] : '';
                                                    
                                                    if (dateMatch) {
                                                        let newValue = `${dateMatch[1]}.${dateMatch[2]}.${dateMatch[3]}`;
                                                        if (hour && minute !== '') {
                                                            const paddedMinute = minute.toString().padStart(2, '0');
                                                            newValue += ` ${hour}:${paddedMinute}`;
                                                        }
                                                        setBannerFormData({...bannerFormData, last_update: newValue});
                                                    }
                                                }}
                                                className="form-control"
                                                style={{ flex: '0.5', textAlign: 'center' }}
                                            />
                                        </div>
                                        <small className="form-help">
                                            날짜는 필수이며, 시간과 분은 선택사항입니다.
                                        </small>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 저장 버튼 */}
                        <div className="settings-actions">
                            <button 
                                className="btn-save-settings"
                                onClick={saveBannerSettings}
                                style={{
                                    padding: '12px 30px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    marginTop: '20px'
                                }}
                            >
                                💾 배너 설정 저장
                            </button>
                            <p style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
                                ⚠️ 배너 설정 (제목, 내용, 이미지), 버튼 설정, 기타 설정을 저장합니다.
                            </p>
                            <p style={{ marginTop: '5px', color: '#999', fontSize: '12px' }}>
                                💡 표시할 배너를 변경하려면 위의 "표시 배너 설정"에서 변경 버튼을 눌러주세요.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <AdminModal
                isOpen={modal.isOpen}
                onClose={closeModal}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onConfirm={modal.onConfirm}
            />
        </div>
    );
};

export default AdminDashboard;
